import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    IconButton,
    Chip,
    Divider
} from '@mui/material';
import {
    Close as CloseIcon,
    Download as DownloadIcon,
    Share as ShareIcon,
    CheckCircle as CheckIcon,
    Cancel as MissingIcon
} from '@mui/icons-material';
import { Document, Packer, Paragraph, HeadingLevel, TextRun } from 'docx';
import { toast } from 'react-toastify';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import ShareModal from './ShareModal';
import ReactMarkdown from 'react-markdown';

const ClientProfileModal = ({ open, onClose, profileData, isLoading }) => {
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);

    // Helper function to parse markdown bold text into TextRun children for Word export
    const parseBoldText = (text) => {
        const children = [];
        const parts = text.split(/(\*\*.*?\*\*)/g);
        for (const part of parts) {
            if (part.startsWith('**') && part.endsWith('**')) {
                children.push(new TextRun({ text: part.slice(2, -2), bold: true }));
            } else if (part) {
                children.push(new TextRun({ text: part }));
            }
        }
        return children;
    };

    // Helper function to parse markdown report text into docx paragraphs
    const parseProfileTextToDocx = (text) => {
        const paragraphs = [];
        const lines = text.split('\n');

        for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;

            if (trimmed.startsWith('#### ')) {
                paragraphs.push(new Paragraph({
                    children: parseBoldText(trimmed.substring(5)),
                    heading: HeadingLevel.HEADING_4,
                    spacing: { before: 180, after: 80 }
                }));
            } else if (trimmed.startsWith('### ')) {
                paragraphs.push(new Paragraph({
                    children: parseBoldText(trimmed.substring(4)),
                    heading: HeadingLevel.HEADING_3,
                    spacing: { before: 200, after: 100 }
                }));
            } else if (trimmed.startsWith('## ')) {
                paragraphs.push(new Paragraph({
                    children: parseBoldText(trimmed.substring(3)),
                    heading: HeadingLevel.HEADING_2,
                    spacing: { before: 250, after: 120 }
                }));
            } else if (trimmed.startsWith('# ')) {
                paragraphs.push(new Paragraph({
                    children: parseBoldText(trimmed.substring(2)),
                    heading: HeadingLevel.HEADING_1,
                    spacing: { before: 300, after: 150 }
                }));
            } else if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
                const bulletText = trimmed.substring(2);
                paragraphs.push(new Paragraph({
                    children: [new TextRun({ text: '• ' }), ...parseBoldText(bulletText)],
                    spacing: { after: 50 },
                    indent: { left: 360 }
                }));
            } else if (trimmed === '---') {
                paragraphs.push(new Paragraph({
                    text: '________________________________',
                    spacing: { before: 200, after: 200 }
                }));
            } else {
                paragraphs.push(new Paragraph({
                    children: parseBoldText(trimmed),
                    spacing: { after: 100 }
                }));
            }
        }

        return paragraphs;
    };

    const handleExportToWord = async () => {
        if (!profileData) {
            toast.error('No profile data to export.');
            return;
        }

        try {
            const doc = new Document({
                sections: [{
                    children: [
                        new Paragraph({
                            text: `Client Profile: ${profileData.companyName}`,
                            heading: HeadingLevel.TITLE
                        }),
                        new Paragraph({
                            text: `Generated: ${new Date(profileData.generatedAt).toLocaleDateString()}`,
                            spacing: { after: 200 }
                        }),
                        new Paragraph({
                            text: `Generated by: ${profileData.generatedBy}`,
                            spacing: { after: 400 }
                        }),
                        ...parseProfileTextToDocx(profileData.content),
                    ],
                }],
            });

            const blob = await Packer.toBlob(doc);
            const filename = `Client_Profile_${profileData.companyName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.docx`;

            const link = document.createElement('a');
            const url = window.URL.createObjectURL(blob);
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success('Profile exported successfully!');
        } catch (error) {
            console.error('Word export failed:', error);
            toast.error('Failed to export Word document.');
        }
    };

    const handleShare = async (sharePayload) => {
    const payloadForBackend = {
        recipientEmail: sharePayload.recipientEmail,
        personalMessage: sharePayload.personalMessage,
        reportData: {
            companyName: profileData.companyName,
            generatedAt: profileData.generatedAt,
            content: profileData.content
        }
    };

    try {
        const shareReport = httpsCallable(functions, 'shareClientProfileByEmail');
        await shareReport(payloadForBackend);
        toast.success('Client Profile shared successfully!');
        return { success: true };
    } catch (error) {
        console.error('Share failed:', error);
        toast.error(`Failed to share profile: ${error.message}`);
        return { success: false, error: error.message };
    }
};

    const DataSourceChip = ({ label, available }) => (
        <Chip
            size="small"
            icon={available ? <CheckIcon fontSize="small" /> : <MissingIcon fontSize="small" />}
            label={label}
            color={available ? 'success' : 'default'}
            variant={available ? 'filled' : 'outlined'}
            sx={{ mr: 0.5, mb: 0.5 }}
        />
    );

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { minHeight: '70vh' }
                }}
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" fontWeight={600}>
                            {isLoading ? 'Generating Client Profile...' : `Client Profile: ${profileData?.companyName || ''}`}
                        </Typography>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent dividers>
                    {isLoading ? (
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            minHeight="300px"
                        >
                            <CircularProgress size={48} sx={{ mb: 2 }} />
                            <Typography variant="body1" color="text.secondary">
                                Synthesizing company information...
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                This may take 2-3 minutes.
                            </Typography>
                        </Box>
                    ) : profileData ? (
                        <Box>
                            {/* Profile Content */}
                            <Box
                                sx={{
                                    '& h1': { fontSize: '1.5rem', fontWeight: 600, mt: 3, mb: 1 },
                                    '& h2': { fontSize: '1.25rem', fontWeight: 600, mt: 2.5, mb: 1 },
                                    '& h3': { fontSize: '1.1rem', fontWeight: 600, mt: 2, mb: 0.5 },
                                    '& h4': { fontSize: '1rem', fontWeight: 600, mt: 1.5, mb: 0.5 },
                                    '& p': { mb: 1.5, lineHeight: 1.6 },
                                    '& ul, & ol': { pl: 3, mb: 1.5 },
                                    '& li': { mb: 0.5 },
                                    '& strong': { fontWeight: 600 },
                                }}
                            >
                                <ReactMarkdown>{profileData.content}</ReactMarkdown>
                            </Box>

                            <Divider sx={{ mt: 3, mb: 1 }} />

                            <Typography variant="caption" color="text.secondary">
                                Generated: {new Date(profileData.generatedAt).toLocaleString()} by {profileData.generatedBy}
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            minHeight="200px"
                        >
                            <Typography color="text.secondary">
                                No profile data available.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={onClose} color="inherit">
                        Close
                    </Button>
                    <Button
                        onClick={() => setIsShareModalOpen(true)}
                        startIcon={<ShareIcon />}
                        disabled={!profileData || isLoading}
                    >
                        Share
                    </Button>
                    <Button
                        onClick={handleExportToWord}
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        disabled={!profileData || isLoading}
                    >
                        Export to Word
                    </Button>
                </DialogActions>
            </Dialog>

            {isShareModalOpen && profileData && (
                <ShareModal
                    open={isShareModalOpen}
                    onClose={() => setIsShareModalOpen(false)}
                    item={profileData}
                    itemType="client_profile"
                    onShare={handleShare}
                />
            )}
        </>
    );
};

export default ClientProfileModal;
